import React, { useState, useEffect } from 'react'
import { 
  UsersIcon, 
  PlusIcon, 
  MagnifyingGlassIcon,
  IdentificationIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon
} from '@heroicons/react/24/outline'
import { conductoresService, Conductor } from '../../../services/conductoresService'
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion'

const PaginaConductores: React.FC = () => {
  const { usuario } = useAuth()
  const idOrganizacion = usuario?.id_organizacion
  const [conductores, setConductores] = useState<Conductor[]>([])
  const [busqueda, setBusqueda] = useState('')
  const [filtroEstado, setFiltroEstado] = useState<string>('todos')
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (typeof idOrganizacion === 'number') {
      cargarConductores(idOrganizacion)
      return
    }

    setConductores([])
    setError('No se encontró una organización activa para consultar los conductores.')
    setCargando(false)
  }, [idOrganizacion])

  const cargarConductores = async (tenantId: number) => {
    try {
      setCargando(true)
      const data = await conductoresService.obtenerConductores(0, 100, tenantId)
      setConductores(data)
      setError(null)
    } catch (error) {
      setError('Error al cargar conductores')
      console.error('Error al cargar conductores:', error)
    } finally {
      setCargando(false)
    }
  }

  // Se usa la variable cargando temporalmente para evitar el warning
  if (cargando) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <p className="text-gray-500">Cargando conductores...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <div className="max-w-lg rounded-xl border border-red-200 bg-red-50 px-6 py-5 text-center text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      </div>
    )
  }

  const conductoresFiltrados = conductores.filter(conductor => {
    const cumpleBusqueda = conductor.numeroLicencia.toLowerCase().includes(busqueda.toLowerCase())
    const cumpleFiltroEstado = filtroEstado === 'todos' || conductor.estado === filtroEstado
    return cumpleBusqueda && cumpleFiltroEstado
  })

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'activo':
        return 'text-green-600 bg-green-100'
      case 'inactivo':
        return 'text-gray-600 bg-gray-100'
      case 'suspendido':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'activo':
        return <CheckCircleIcon className="h-4 w-4" />
      case 'suspendido':
        return <XCircleIcon className="h-4 w-4" />
      default:
        return <CheckCircleIcon className="h-4 w-4" />
    }
  }

  const isLicenciaVencida = (fecha: string) => {
    return new Date(fecha) < new Date()
  }

  const isLicenciaPorVencer = (fecha: string) => {
    const fechaVencimiento = new Date(fecha)
    const fechaActual = new Date()
    const diasDiferencia = Math.ceil((fechaVencimiento.getTime() - fechaActual.getTime()) / (1000 * 3600 * 24))
    return diasDiferencia <= 30 && diasDiferencia > 0
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Conductores</h1>
            <p className="text-gray-600 dark:text-gray-400">Gestiona la información de los conductores de la flota</p>
          </div>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
            <PlusIcon className="h-5 w-5" />
            <span>Agregar Conductor</span>
          </button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <UsersIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Conductores</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{conductores.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Activos</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {conductores.filter(c => c.estado === 'activo').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <IdentificationIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Licencias por Vencer</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {conductores.filter(c => c.vencimientoLicencia && isLicenciaPorVencer(c.vencimientoLicencia)).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Licencias Vencidas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {conductores.filter(c => c.vencimientoLicencia && isLicenciaVencida(c.vencimientoLicencia)).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-64">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, apellido o cédula..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-10 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="todos">Todos los estados</option>
            <option value="activo">Activo</option>
            <option value="inactivo">Inactivo</option>
            <option value="suspendido">Suspendido</option>
          </select>
        </div>
      </div>

      {/* Lista de conductores */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Conductor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Contacto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Licencia
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Vehículo Asignado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {conductoresFiltrados.map((conductor) => (
                <tr key={conductor.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {conductor.idUsuario ? `Usuario ID: ${conductor.idUsuario}` : 'Sin Usuario'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Licencia: {conductor.numeroLicencia}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-gray-900 dark:text-white">
                        <IdentificationIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {conductor.tipoLicencia}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        Categoría {conductor.tipoLicencia}
                      </div>
                      <div className={`text-sm ${
                        conductor.vencimientoLicencia && isLicenciaVencida(conductor.vencimientoLicencia) 
                          ? 'text-red-600' 
                          : conductor.vencimientoLicencia && isLicenciaPorVencer(conductor.vencimientoLicencia)
                          ? 'text-yellow-600'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        Vence: {conductor.vencimientoLicencia ? new Date(conductor.vencimientoLicencia).toLocaleDateString('es-ES') : 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    Sin asignar
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEstadoColor(conductor.estado)}`}>
                      {getEstadoIcon(conductor.estado)}
                      <span className="ml-1 capitalize">{conductor.estado}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {conductoresFiltrados.length === 0 && (
          <div className="text-center py-8">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No hay conductores</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No se encontraron conductores con los criterios de búsqueda.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PaginaConductores
